// src/pages/TermsOfService.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useBranding } from '../context/BrandingContext';
import { useRegionalSettings } from '../context/RegionalSettingsContext';
import { EU_PAYMENT_COUNTRY_CODES } from '../config/countryProfiles';

function Section({ title, children }) {
    return (
        <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            <div className="text-sm text-gray-700 space-y-2 leading-relaxed">{children}</div>
        </section>
    );
}

export default function TermsOfService() {
    const { t } = useTranslation();
    const { appName } = useBranding();
    const { settings } = useRegionalSettings();
    const name = appName || 'ChessDesk';
    const cc = String(settings?.countryCode || '').toUpperCase();
    const isEu = EU_PAYMENT_COUNTRY_CODES.has(cc) || cc === 'GB';

    return (
        <div className="min-h-screen bg-gray-50 py-10 px-4">
            <div className="mx-auto max-w-3xl bg-white rounded-2xl shadow-sm border border-gray-100 px-8 py-10 space-y-8">
                <div className="space-y-1">
                    <Link to="/login" className="text-xs text-blue-600 hover:underline">{t('legal.backToLogin')}</Link>
                    <h1 className="text-2xl font-bold text-gray-900 mt-2">{t('legal.termsTitle')}</h1>
                    <p className="text-xs text-gray-400">{t('legal.lastUpdated', { name })}</p>
                </div>

                <Section title={t('terms.agreement.title')}>
                    <p>
                        <Trans
                            i18nKey="terms.agreement.body"
                            values={{ name }}
                            components={{
                                strong: <strong />,
                                privacyLink: <Link to="/privacy-policy" className="underline text-blue-600" />,
                            }}
                        />
                    </p>
                </Section>

                <Section title={t('terms.licence.title')}>
                    <p>{t('terms.licence.body')}</p>
                </Section>

                <Section title={t('terms.fees.title')}>
                    <p>{t('terms.fees.body1')}</p>
                    <p>{isEu ? t('terms.fees.euLicence') : t('terms.fees.nonEuLicence')}</p>
                </Section>

                <Section title={t('terms.support.title')}>
                    <p>
                        {t('terms.support.body')}
                        {isEu ? t('terms.support.euHours') : t('terms.support.indiaHours')}
                    </p>
                </Section>

                <Section title={t('terms.liability.title')}>
                    <p>{t('terms.liability.body')}</p>
                </Section>

                <Section title={t('terms.governingLaw.title')}>
                    {isEu ? (
                        <p>
                            <Trans
                                i18nKey="terms.governingLaw.euBody"
                                components={{ code: <code className="text-xs" /> }}
                            />
                        </p>
                    ) : (
                        <p>{t('terms.governingLaw.indiaBody')}</p>
                    )}
                </Section>

                <div className="border-t border-gray-100 pt-6 text-xs text-gray-400">
                    <p>{t('terms.footer', { name })}</p>
                </div>
            </div>
        </div>
    );
}
